import React, { useEffect, useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'
import {
  CertificateGroupMemberPreview,
  CertificateGroupMemberPreviewSkeleton,
  DivisibilityOptionPicker,
  ProductPreview,
  // VRG-231 — the SAME scan control the Certificates list toolbar renders beside
  // its own search box (`ActionLine`'s `showScan`), and the same one every filter
  // field renders (`SearchFilter`). Not a second implementation: per §28/§36 the
  // control's three states, wording, glyph, and source-picker all live once in
  // `ScanTargetButton`, and this surface supplies only what is local to it (which
  // target the scans aim at, and how that target is named in the copy).
  ScanTargetButton,
  Search,
  SearchFilter,
  // VRG-231 review item 2 — reused for the parent-product row's sub-line (the
  // certificate that seeded the parent's product, shown as an id rather than
  // the ambiguous product SKU); exported from the shared member-preview
  // component (§36) rather than re-implemented here.
  shortCertificateId,
} from 'components'

import { useScanRouting, useSnackbar } from 'contexts'
import {
  useCertificateGroupCandidates,
  useCreateGroupFromExisting,
  useLinkCertificatesToGroup,
  useProducts,
} from 'hooks/api'
import { getErrorMessage } from 'utils'
import type { CertificateAdmin, CertificateGroupDivisibility, Product } from 'types'

interface CreateCertificateGroupModalProps {
  open: boolean
  onClose: () => void
  /** Pre-seed the picker with one certificate (row kebab / detail-page "Create
   *  group" entry points). That certificate supplies the PARENT's product and is
   *  excluded from the member list; the user then picks the members. VRG-231
   *  (2026-08-30): a group needs >= 1 member, so this path now completes with a
   *  single pick. Ignored when `initialCertificates` is also supplied (callers
   *  only ever set one or the other). */
  initialCertificate?: CertificateAdmin | null
  /** VRG-231 item C — pre-seed the picker with MULTIPLE certificates (the
   *  Certificates list's bulk-bar "Group certificates" entry point). Takes
   *  precedence over `initialCertificate` when both are present. The caller
   *  is expected to have already filtered out ineligible rows and reported
   *  the drop (see `CertificatesPage.handleOpenCreateGroupModalBulk`); this
   *  modal re-checks with `getIneligibleReason` regardless, as defense in
   *  depth, and silently drops anything that still fails rather than trusting
   *  the caller blindly. */
  initialCertificates?: CertificateAdmin[]
  /** VRG-231 (add-to-existing-group) — when set, this modal operates in "add
   *  to existing group" mode instead of "create a new group": the parent is
   *  FIXED (shown read-only, not a product picker), divisibility is inherited
   *  from the parent and hidden (immutable once a group exists), and
   *  submission calls the link-to-existing-group endpoint. `initialCertificates`
   *  is still used in this mode to pre-seed the member picker with the OTHER
   *  selected rows (the parent itself is excluded automatically). */
  existingParent?: CertificateAdmin | null
  /** Called after the group is created/updated — callers refetch their list/detail data. */
  onCreated?: () => void
}

/**
 * VRG-231 — the scan routing key for THIS modal's member search.
 *
 * Deliberately NOT `SEARCH_SCAN_TARGET_KEY`: the Certificates list's own toolbar
 * search is still mounted behind this modal and holds that key, and a target's
 * identity is `kind` + `key` (see `ScanRoutingTarget`). Sharing the key would make
 * both controls read as aimed at once and have both consume the same scanned
 * queue. A distinct key keeps the app's single target slot honest — aiming this
 * control un-aims the list's, which is exactly the intent while the modal is open.
 */
const GROUP_MEMBER_SEARCH_SCAN_KEY = 'group-member-search'

// VRG-337 — why a certificate is not selectable in the picker. Mirrors the
// server-side preconditions in GroupExistingCertificatesUseCase exactly, so
// the reason shown here always matches what the API would reject with.
// Exported (VRG-231 item C) so the Certificates list's bulk-bar handler can
// filter/report ineligible rows using the exact same rule before ever opening
// this modal.
export const getIneligibleReason = (certificate: CertificateAdmin): string | null => {
  if (certificate.parent_certificate_id || certificate.group_divisibility) {
    return 'Already part of a certificate group'
  }
  // customer_id/customer = a CURRENT owner; date_of_ownership is set the first
  // time a certificate is ever owned and never cleared afterward (see
  // GroupExistingCertificatesUseCase) — so it also catches a certificate that
  // was transferred and later returned to the brand.
  if (certificate.customer_id || (certificate as any).customer || certificate.date_of_ownership) {
    return 'Has an owner (or ownership history) and cannot be retroactively grouped'
  }
  return null
}

/**
 * VRG-337 — "Group existing certificates": pick >= 1 already-existing,
 * standalone certificate(s), choose divisibility + the new parent's product,
 * and create the group. Replaces the VRG-231 placeholder shared by the
 * certificate detail header overflow and the Certificates list row kebab.
 * (The floor was >= 2 until VRG-231, 2026-08-30 — see `canSubmit` below.)
 *
 * Member picker follows the shared search + multi-select-checkbox pattern
 * (§28 — AssignUsersModal), rendering each row with the shared
 * CertificateGroupMemberPreview in its selectable-checkbox mode (§36).
 * Divisibility uses the same selectable-card treatment as the create wizard
 * (§34), via the shared DivisibilityOptionPicker (also extracted here, on its
 * second hand-roll — see components/input/DivisibilityOptionPicker).
 */
export const CreateCertificateGroupModal = (props: CreateCertificateGroupModalProps) => {
  const { open, onClose, initialCertificate, initialCertificates, existingParent, onCreated } = props
  const { open: notification } = useSnackbar()
  // VRG-231 — the session itself lives in `ScanRoutingProvider` (mounted in
  // `Layout`, above every page), so it is reachable from inside a modal and
  // survives this modal closing. This surface only aims at it and reads what
  // landed; it never owns it.
  const scanning = useScanRouting()
  const isAddToExistingGroup = Boolean(existingParent)

  const [search, setSearch] = useState('')
  const [selectedMap, setSelectedMap] = useState<Map<string, CertificateAdmin>>(new Map())
  const [divisibility, setDivisibility] = useState<CertificateGroupDivisibility | null>(null)
  const [parentProduct, setParentProduct] = useState<Product | null>(null)
  const [ineligibleNotice, setIneligibleNotice] = useState<string | null>(null)
  // VRG-231 review items 2 + 4 — the certificate the parent's product was
  // seeded from (the "Create group" kebab entry point), when there is one.
  // Two jobs: (a) the parent-product row's sub-line shows THIS id instead of
  // the ambiguous product SKU, and (b) that certificate is excluded from the
  // member candidates below — it donated its product to the parent, it isn't
  // itself one of the certificates being grouped. Cleared whenever the parent
  // product is changed/cleared, since at that point it no longer represents
  // that certificate's context and is fair game to pick as a member again.
  const [seededParentCertificateId, setSeededParentCertificateId] = useState<string | null>(null)

  // Reset + seed every time the modal OPENS (not just on initialCertificate/
  // initialCertificates changing — mirrors CreateCertificatesModal's own
  // "preselect on open" note: keying only on the prop would leave stale state
  // on a close+reopen with the same certificate(s)).
  useEffect(() => {
    if (!open) return

    setSearch('')
    setDivisibility(null)
    setIneligibleNotice(null)

    // VRG-231 (add-to-existing-group) — takes precedence over everything
    // below: the parent is fixed (not derived from a seeded product), and
    // `initialCertificates` here is just the OTHER rows the bulk selection
    // already picked (the parent itself is excluded via
    // `seededParentCertificateId`, same mechanism the single-certificate path
    // below uses to exclude ITS seed certificate).
    if (existingParent) {
      const eligible = (initialCertificates ?? []).filter(certificate => !getIneligibleReason(certificate))
      setSelectedMap(new Map(eligible.map(certificate => [certificate.id, certificate])))
      setParentProduct(null)
      setSeededParentCertificateId(existingParent.id)
      return
    }

    // VRG-231 item C — the bulk-bar entry point. Takes precedence over the
    // singular `initialCertificate` (callers only ever set one). The caller
    // (CertificatesPage.handleOpenCreateGroupModalBulk) has already filtered
    // ineligible rows and told the user how many were dropped, so this is a
    // defensive re-check, not the primary filter — anything that still fails
    // `getIneligibleReason` here is silently excluded rather than surfaced
    // again (the bulk handler already surfaced it once).
    if (initialCertificates && initialCertificates.length > 0) {
      const eligible = initialCertificates.filter(certificate => !getIneligibleReason(certificate))
      setSelectedMap(new Map(eligible.map(certificate => [certificate.id, certificate])))
      // Members can come from different products, so — unlike the single-
      // certificate path below — there's no single "obvious" parent product
      // to default to. Left unset; the user picks one explicitly. No single
      // certificate seeded this pick either, so nothing is excluded below.
      setParentProduct(null)
      setSeededParentCertificateId(null)
      return
    }

    if (initialCertificate) {
      const reason = getIneligibleReason(initialCertificate)
      if (reason) {
        setSelectedMap(new Map())
        setParentProduct(null)
        setSeededParentCertificateId(null)
        setIneligibleNotice(
          `The certificate you started from can't be grouped: ${reason.toLowerCase()}. Pick different certificates below.`,
        )
      } else {
        // VRG-231 fix item 4 — this certificate supplies the PARENT's product
        // context; it is not itself a member. It previously also landed in
        // selectedMap, so it showed up a second time in the member picker
        // below (once as the reason the parent product was pre-filled, once
        // as a selectable/selected row) — a certificate can't be both.
        setSelectedMap(new Map())
        setParentProduct(initialCertificate.product ?? null)
        setSeededParentCertificateId(initialCertificate.id)
      }
    } else {
      setSelectedMap(new Map())
      setParentProduct(null)
      setSeededParentCertificateId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingParent?.id, initialCertificate?.id, initialCertificates])

  const { data: candidates, isLoading: isLoadingCandidates } = useCertificateGroupCandidates(
    { query: search, limit: 50 },
    { enabled: open },
  )

  const { data: productsData, isLoading: isLoadingProducts } = useProducts({}, { enabled: open })
  const products = productsData?.data ?? []

  /**
   * VRG-231 — WHERE A SCAN LANDS while this modal's scan control is lit: the tag
   * id goes into the member search box, exactly as it does on the Certificates
   * list (`ActionLine`). The candidates query hits the same `/certificates/admin`
   * endpoint that list does, and that endpoint's `query` matches on tag id
   * server-side, so a tapped tag narrows the picker to that one certificate.
   *
   * Routing into the visible search box rather than auto-checking a row is the
   * same argument the list makes: the operator can SEE what happened, read it,
   * clear it, or type over it. It also degrades correctly when the scanned tag
   * resolves to a certificate that is ineligible or simply absent — the list
   * shows the row (disabled, with its reason) or shows "No certificates found",
   * either of which is legible, where a silent auto-select would not be.
   *
   * One value at a time: a search box holds one string, so a second scan
   * replaces the first. This modal is always mounted (the parent controls it via
   * `open`), so both effects gate on `open` — otherwise a scan aimed at the list
   * behind it would be consumed by a closed modal.
   */
  const scannedForMemberSearch = scanning.scannedFor(GROUP_MEMBER_SEARCH_SCAN_KEY)
  const latestScan = scannedForMemberSearch[scannedForMemberSearch.length - 1]
  useEffect(() => {
    if (!open || !latestScan) return
    setSearch(latestScan.tagId)
    // Hand it to the input and drop it here — the input is now the record of what
    // was scanned; a copy left in the routing layer would re-apply on re-render.
    scanning.clearScanned(GROUP_MEMBER_SEARCH_SCAN_KEY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, latestScan?.tagId])

  // Tell the routing layer this control is on screen, so a landing the operator
  // can watch happen is not ALSO announced as a toast (see `land` in
  // ScanRoutingContext). Unregisters when the modal closes.
  useEffect(() => {
    if (!open) return
    return scanning.registerField(GROUP_MEMBER_SEARCH_SCAN_KEY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scanning.registerField])

  const { createGroupFromExisting, isCreatingGroup } = useCreateGroupFromExisting({
    onSuccess: () => {
      notification({ type: 'success', title: 'Certificate group created' })
      onCreated?.()
      onClose()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const { linkCertificatesToGroup, isLinkingCertificates } = useLinkCertificatesToGroup({
    onSuccess: () => {
      notification({ type: 'success', title: 'Certificates added to group' })
      onCreated?.()
      onClose()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const isSubmitting = isAddToExistingGroup ? isLinkingCertificates : isCreatingGroup

  const toggleSelected = (certificate: CertificateAdmin) => {
    // VRG-231 — silent guard, not a toast. An ineligible row is now rendered
    // visibly disabled with its reason on the row (see the list below), so it is
    // unreachable by click and the toast that used to fire here would only ever
    // repeat what is already on screen. Kept as a no-op guard rather than deleted
    // because this modal's defensive posture is that nothing ineligible enters
    // `selectedMap` regardless of which path reached here.
    if (getIneligibleReason(certificate)) return
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (next.has(certificate.id)) {
        next.delete(certificate.id)
      } else {
        next.set(certificate.id, certificate)
      }
      return next
    })
  }

  const selectedCertificates = useMemo(() => Array.from(selectedMap.values()), [selectedMap])

  // Merge the current search page with any selected members not present in it
  // (e.g. the pinned initial certificate, or a member picked before the search
  // term changed) so a selection never silently disappears from view.
  // VRG-231 fix item 4 — the seeded-parent certificate (see
  // `seededParentCertificateId` above) is excluded here too, not just left
  // unselected: it donated its product to the parent, so it shouldn't be
  // offered as a member candidate at all while that relationship holds.
  const rows = useMemo(() => {
    const seen = new Set<string>()
    const merged: CertificateAdmin[] = []
    for (const cert of [...selectedCertificates, ...candidates]) {
      if (cert.id === seededParentCertificateId) continue
      if (seen.has(cert.id)) continue
      seen.add(cert.id)
      merged.push(cert)
    }
    return merged
  }, [selectedCertificates, candidates, seededParentCertificateId])

  // VRG-231 review item 2 — approved product decision: the parent product is
  // REQUIRED when the group is indivisible (the parent IS the sellable unit,
  // so billing/inventory hang off its product) and OPTIONAL when divisible
  // (the parent is just a container there). `certificates.product_id` is
  // nullable, so an unset parent product is a legal divisible group.
  const parentProductRequired = divisibility === 'indivisible'
  const parentProductSatisfied = !parentProductRequired || Boolean(parentProduct?.id)

  // VRG-231 (2026-08-30) — the create path's floor is ONE member, not two,
  // matching the API guards it mirrors (CreateCertificateGroupUseCase /
  // GroupExistingCertificatesUseCase, both lowered 2 -> 1 the same day). A
  // tagless parent with a single child is a shape the unlink path already
  // preserves (it dissolves a group only at ZERO children), so it was reachable
  // by removal and not by creation. The add-to-existing path was already at 1
  // and is unchanged. Zero stays rejected on BOTH paths.
  // VRG-231 review round 6 — the floor is per-DIVISIBILITY on the create path:
  // 1 for divisible, 2 for indivisible. "Indivisible" means the members are sold
  // as one unit, and one member already IS one unit, so a group of one adds a
  // tagless parent and a second certificate id that describe nothing the child
  // did not. Mirrors the server guard in CreateCertificateGroupUseCase and
  // GroupExistingCertificatesUseCase — this only spares the user a round trip;
  // the server is the boundary.
  //
  // The ADD-TO-EXISTING path keeps a floor of 1 regardless: adding one member to
  // a group that already exists is not creating a group of one.
  const minimumMembers = divisibility === 'indivisible' ? 2 : 1
  const canSubmit = isAddToExistingGroup
    ? selectedCertificates.length >= 1 && !isSubmitting
    : selectedCertificates.length >= minimumMembers && Boolean(divisibility) && parentProductSatisfied && !isSubmitting

  // One reason at a time, in the order a user would naturally resolve them —
  // surfaced on the disabled Create button (@repo/ui Button's `disabledReason`,
  // which explains a disabled control instead of swallowing the click, per
  // working-conventions §46's principle applied to a button rather than a menu
  // row).
  const disabledReason = isSubmitting
    ? undefined
    : isAddToExistingGroup
      ? selectedCertificates.length < 1
        ? 'Select at least 1 certificate to add'
        : undefined
      : !divisibility
        ? 'Choose divisible or indivisible'
        : selectedCertificates.length < minimumMembers
          ? minimumMembers === 2
            ? 'An indivisible group needs at least 2 certificates'
            : 'Select at least 1 certificate to group'
          : !parentProductSatisfied
            ? 'Choose a parent product — required for an indivisible group'
            : undefined

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    if (isAddToExistingGroup && existingParent) {
      await linkCertificatesToGroup({
        certificateId: existingParent.id,
        childIds: selectedCertificates.map(c => c.id),
      })
      return
    }

    if (!divisibility) return
    await createGroupFromExisting({
      certificateIds: selectedCertificates.map((c) => c.id),
      divisibility,
      // Optional for a divisible group (see `parentProductRequired` above).
      // `useCreateGroupFromExisting`'s current type requires a string —
      // that's an API-side/frontend-plumbing concern outside this modal's
      // owned files (flagged separately), so this cast reflects the real,
      // legal (nullable `product_id`) payload without editing that hook.
      parentProductId: parentProduct?.id as string,
    })
  }

  return (
    <Modal
      title={isAddToExistingGroup ? 'Add to group' : 'Group certificates'}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            disabledReason={disabledReason}
            loading={isSubmitting}
          >
            {isAddToExistingGroup ? 'Add to group' : 'Create group'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {/* VRG-231 review item 1 — shortened: dropped "brand" (redundant — every
            certificate here already belongs to this brand) and dropped the
            "already sold or given to an owner is out of scope" sentence
            (implied, and the modal already refuses ineligible rows). */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isAddToExistingGroup
            ? 'Add certificates to this existing group. Each member keeps its own product, history, and tag.'
            : 'Group existing certificates under one parent certificate with its own components. Each member keeps its own product, history, and tag.'}
        </Typography>

        {ineligibleNotice && <Alert severity="warning" description={ineligibleNotice} />}

        {isAddToExistingGroup && existingParent ? (
          // VRG-231 (add-to-existing-group) — the parent is fixed: divisibility
          // is immutable once a group exists (see LinkCertificatesToGroupPayloadDto
          // on the API), so it's shown, never editable, and there's no product
          // picker — the existing parent already has one.
          <Stack spacing={2}>
            <Typography variant="body2">Group</Typography>
            <CertificateGroupMemberPreview certificate={existingParent} role="parent" />
          </Stack>
        ) : (
          <>
            <Stack spacing={2}>
              <Typography variant="body2">Divisibility</Typography>
              <DivisibilityOptionPicker value={divisibility} onChange={setDivisibility} disabled={isSubmitting} />
            </Stack>

            <Stack spacing={2}>
              {/* "(optional)" mirrors the divisible/indivisible rule below — a plain
                  "Parent product" label would read as always-required, which isn't
                  true for a divisible group. */}
              <Typography variant="body2">
                Parent product{parentProductRequired ? '' : ' (optional)'}
              </Typography>
              {parentProduct ? (
                <ProductPreview
                  product={parentProduct}
                  disabled={isSubmitting}
                  onChange={() => {
                    setParentProduct(null)
                    setSeededParentCertificateId(null)
                  }}
                  // VRG-231 review item 2 — when this pick came from a specific
                  // certificate (the "Create group" kebab entry point), show THAT
                  // certificate's id instead of the default product SKU: the SKU
                  // doesn't disambiguate which certificate was started from (many
                  // certificates can share a product/SKU), the id does. Falls back
                  // to the normal SKU sub-line for a manually-picked product.
                  description={
                    seededParentCertificateId
                      ? `Certificate ${shortCertificateId(seededParentCertificateId)}`
                      : undefined
                  }
                />
              ) : (
                <Stack spacing={2}>
                  {/* VRG-231 review item 2 — the "Select a product" placeholder line
                      above the search box was removed (redundant with the "Parent
                      product" label right above it). */}
                  <SearchFilter
                    notFilter
                    singleSelect
                    column={{ field: 'product' } as any}
                    rows={products as any}
                    filter={[] as any}
                    searchParams={{ fields: ['title', 'sku'], preview: 'products' }}
                    onChange={((item: any) => {
                      setParentProduct(item)
                      setSeededParentCertificateId(null)
                    }) as any}
                  />
                  {isLoadingProducts && (
                    <Typography variant="body3" color="text.secondary">
                      Loading products…
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </>
        )}

        <Stack spacing={2}>
          <Typography variant="body2">
            Members{selectedCertificates.length > 0 ? ` (${selectedCertificates.length} selected)` : ''}
          </Typography>
          {/* VRG-231 — the scan control sits OUTSIDE the field, as a separate
              adjacent button, because that is what it is everywhere else in the
              app (the list toolbar and every filter field). It is a stateful mode
              toggle that aims an ongoing scan session, not an adornment acting on
              this field's value. `spacing={3}` = 12px, the button-group gap (§29);
              `ScanTargetButton` defaults to `large` (49px) to match the search
              field's height, which is the same pairing it has on the toolbar. */}
          <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
            {/* VRG-231 (2026-08-30) — THE SEARCH FLEXES; THE SCAN BUTTON IS FIXED.
                The field used to stop 129px short of the modal's right edge, with the
                scan button stranded mid-row and dead space beyond it. Measured before
                the fix: row 499px, search 300px, scan 58px, 129px empty.

                `fullWidth` on `Search` was already set and did nothing, which is the
                part worth writing down. `@repo/ui`'s `Input` wraps its control in a
                bare `<Box>` with no width of its own (Input.tsx ~:456, added so a
                field is ONE layout child per working-conventions §31). `display:
                block` + `width: auto` makes that Box shrink-to-fit as a flex item, so
                its flex base size came from its max-content — which `Search`'s own
                `minWidth: 300` pins at exactly 300px. `fullWidth` then resolved
                `width: 100%` against that 300px parent and changed nothing.

                `Search`'s `sx` cannot reach that Box either: it is spread onto the
                inner `Input`'s `sx`, one level below. Hence this explicit flex
                wrapper. `minWidth: 0` because a flex item's default `min-width: auto`
                would refuse to shrink below the field's 300px floor on a narrow
                viewport and push the scan button out of the modal.

                `width: '100%'` on the Stack itself is belt-and-braces for the same
                shrink-to-fit hazard one level up. `spacing={3}` (12px, the house
                button-group gap, §29) is unchanged — it was already correct and
                measured at 12px. */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Search
                value={search}
                onChange={setSearch}
                onClear={() => setSearch('')}
                placeholder="Search certificates"
                fullWidth
              />
            </Box>
            <ScanTargetButton
              target={{
                kind: 'search',
                key: GROUP_MEMBER_SEARCH_SCAN_KEY,
                // A tag resolves to its certificate here, same as the list: this
                // picker is choosing certificates, and the search matches tag id.
                entity: 'certificate',
                label: 'Member search',
              }}
              destination="the member search"
            />
          </Stack>
          {/* VRG-231 fix E — was a fixed `maxHeight: 360`. On top of the description +
              divisibility picker + parent-product row + this search box, a fixed 360px
              list left too little room at a 1280x800 viewport for the modal's own body
              scroll region to comfortably surface both the list AND the action bar
              without excess internal scrolling. `min(360px, 32vh)` keeps the generous
              360px cap on tall windows but shrinks proportionally on short ones, so the
              rest of the modal (and its Cancel/Create action bar, which the shared
              `Modal` already keeps in a non-scrolling `flexShrink: 0` footer — see
              packages/ui/components/feedback/Modal/Modal.tsx) has more room to stay
              on-screen without the user needing to scroll the modal body first. */}
          <Box sx={{ maxHeight: 'min(360px, 32vh)', overflowY: 'auto' }}>
            <Stack spacing={2}>
              {isLoadingCandidates && rows.length === 0 ? (
                <>
                  <CertificateGroupMemberPreviewSkeleton />
                  <CertificateGroupMemberPreviewSkeleton />
                  <CertificateGroupMemberPreviewSkeleton />
                </>
              ) : rows.length === 0 ? (
                <Typography variant="body3" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No certificates found.
                </Typography>
              ) : (
                rows.map((certificate) => {
                  const reason = getIneligibleReason(certificate)

                  /**
                   * VRG-231 — AN INELIGIBLE ROW STAYS IN THE LIST, VISIBLY
                   * DISABLED, WITH ITS REASON READABLE WITHOUT A CLICK.
                   *
                   * It previously looked identical to a selectable row and only
                   * revealed itself by firing an error toast when you clicked it:
                   * click-to-discover, which costs the operator a wasted action
                   * per ineligible certificate and teaches nothing until then.
                   *
                   * HIDING THEM WAS THE ALTERNATIVE AND WAS REJECTED, and this is
                   * the non-obvious half: an ABSENT row is unexplainable. An
                   * operator who knows they own a given certificate and cannot
                   * find it here cannot tell whether it is ineligible, filtered
                   * out, on another page, or whether search is broken — four very
                   * different situations that all render as nothing. A disabled
                   * row carrying its reason answers that on sight.
                   *
                   * This is a DELIBERATE departure from working-conventions §46,
                   * whose default is that record state HIDES and only a permission
                   * disables. §46's hide rule is justified by its own load-bearing
                   * clause: hiding creates an obligation to make the RESOLVING
                   * action reachable in that state. Inside a picker modal it is
                   * not — the resolution for "already part of a certificate group"
                   * is to split it out of that group, which lives on the
                   * certificate's own page and cannot be reached from here. So
                   * hiding would strand the user with neither the action nor its
                   * resolution, which is precisely the dead end §46 warns about.
                   * §46's forcing function is satisfied either way: we CAN write
                   * the sentence explaining the block, and we do — on the row.
                   *
                   * The reason strings come from `getIneligibleReason` (the single
                   * source that mirrors the server's own preconditions), never
                   * retyped here — a retyped copy is drift by construction.
                   */
                  if (reason) {
                    return (
                      <Box
                        key={certificate.id}
                        aria-disabled
                        sx={{ cursor: 'not-allowed' }}
                      >
                        <Box
                          sx={{
                            opacity: 0.5,
                            // Dims the row and suppresses its hover/focus brand
                            // border, so it offers no affordance it cannot honour.
                            //
                            // VRG-231 review round 3 — this used to ALSO carry
                            // `pointerEvents: 'none'` and a `.MuiCheckbox-root`
                            // colour override, because the shared row had no
                            // `disabled` prop. Both are gone: the row now takes a
                            // real `disabled` (below), which puts the actual
                            // `disabled` attribute on the actual input. The
                            // workaround stopped the CLICK but left the checkbox
                            // announced as enabled to a screen reader, offering a
                            // control that does nothing.
                          }}
                        >
                          <CertificateGroupMemberPreview
                            certificate={certificate}
                            selected={false}
                            // Still in checkbox mode, not link mode: passing a
                            // handler is what keeps it a picker row. Without it the
                            // row falls back to a navigation Link, which is wrong
                            // twice over — wrong affordance, and it leaves the modal.
                            onToggleSelected={() => undefined}
                            disabled
                          />
                        </Box>
                        {/* On the row and on sight, not in a tooltip. A tooltip
                            alone would satisfy "not a click" while still hiding
                            the reason until the operator happens to hover the one
                            row they were already puzzled by, which is the state
                            this change exists to remove. Costs ~20px on ineligible
                            rows only; eligible rows are untouched. */}
                        <Typography
                          variant="body3"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1, pl: 4, lineHeight: 1.5 }}
                        >
                          {reason}
                        </Typography>
                      </Box>
                    )
                  }

                  return (
                    <Box key={certificate.id}>
                      <CertificateGroupMemberPreview
                        certificate={certificate}
                        selected={selectedMap.has(certificate.id)}
                        onToggleSelected={() => toggleSelected(certificate)}
                      />
                    </Box>
                  )
                })
              )}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Modal>
  )
}
